/**
 * 구독·결제 (261011-subscription-billing-payup).
 *
 * 흐름: 운영자가 브랜드별로 구독을 **제안**(이름·공급가) → 그 브랜드 소속자가 자기 카드를 골라 **시작**
 * → 첫 결제 성공일이 구독일. 그 뒤 매월 구독일에 갱신 결제.
 * 카드는 **계정 소유**다 — 등록한 계정이 지금 소속된 브랜드 전부의 구독에 쓸 수 있다 (261013-account-brand-membership)
 *
 *  - 결제자 계약(`/api/billing/*`)은 `payer` 다 — 결제 범위가 고객 범위와 다르다(결제 대기 `confirmed` 고객 포함).
 *    범위는 서비스가 계산하고, 운영자는 이 계약을 쓰지 않는다(운영자는 `/api/admin/billing/*`).
 *  - **해지·결제 취소·환불은 운영자만** 한다. 결제자 계약에 그런 쓰기가 없다.
 *  - 비-admin 응답에 PG 응답·거래번호·남의 카드 정보를 싣지 않는다(이 파일의 결제자 모양에 그 칸이 없다).
 */
import { z } from 'zod';
import { BrandKindQuery, IdParams, IsoDateTime, KstDate, Ok } from '../common.ts';
import { defineRoute } from '../route.ts';

export const SUBSCRIPTION_STATUSES = ['offered', 'active', 'past_due', 'canceled'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const CHARGE_STATUSES = ['pending', 'paid', 'failed', 'refunded'] as const;
export type ChargeStatus = (typeof CHARGE_STATUSES)[number];

/**
 * declined    PG 가 거절했다(돈이 나가지 않았다) — 결제자가 다시 시도할 수 있다
 * no_response 결과를 모른다(타임아웃·네트워크·5xx·응답 불일치·처리 중 중단). **실패로 보되 운영자 검토 전까지 결제를 막는다**
 * ineligible  카드 자격이 없다(카드 주인이 그 브랜드 소속에서 빠졌다·카드 삭제) — PG 를 부르지 않았다
 */
export const FAILURE_REASONS = ['declined', 'no_response', 'ineligible'] as const;
export type FailureReason = (typeof FAILURE_REASONS)[number];

/**
 * requested 고객 요청 해지 · admin 운영자 판단 · brand_deleted 브랜드 삭제(= 계약 종료, 261022-brand-delete-is-contract-end) ·
 * withdrawn **제안 철회**(탈퇴가 아니다)
 */
export const CANCEL_REASONS = ['requested', 'admin', 'brand_deleted', 'withdrawn'] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

/**
 * 구독 이름의 기본값 — 운영자가 제안할 때 미리 채운다(운영자 결정 2026-10-12). 고객 결제 화면과 페이업 결제 알림톡
 * 상품명(`liveGPT {이름}`)에 그대로 나간다. ★ 화면 문구의 `GEO` 금지(브랜드 보이스)에서 괄호 풀이 형태로 쓴다
 */
export const DEFAULT_SUBSCRIPTION_NAME = 'AI검색최적화(GEO) 마케팅';

/**
 * 구독 서비스 이용약관의 판(시행일) — 서면 계약서를 갈음하는 약관이다(web/src/legal/content/subscription.ts).
 * ★ 문안을 고치면 올린다. 구독마다 동의한 판이 남고(`terms_version`), 서버는 **현재 판이 아닌 동의를 거부**한다
 */
export const SUBSCRIPTION_TERMS_VERSION = '2026-10-20';

/** 공급가 상한 — 오타(0 하나 더)로 큰돈이 결제되지 않게 */
export const SUPPLY_PRICE_MAX = 100_000_000;

/**
 * 카드 입력 — **요청 전용**이다. 응답에 절대 싣지 않는다.
 * 서버는 받는 즉시 페이업 `/ready` 로 넘기고 지역변수로만 다룬다. 로그·DB·오류 어디에도 남기지 않는다.
 * ★ 검증 메시지에 입력값을 넣지 않는다 — 오류 응답·로그로 카드번호가 새지 않게(기본 메시지만 쓴다).
 */
export const CardInput = z
  .object({
    /** 카드번호 13~19자리 숫자 */
    pinNumber: z.string().regex(/^\d{13,19}$/),
    /** 유효기간 MMYY */
    expireDate: z.string().regex(/^(0[1-9]|1[0-2])\d{2}$/),
    /** 생년월일 6자리(개인) 또는 사업자번호 10자리(법인) */
    socialNumber: z.string().regex(/^(\d{6}|\d{10})$/),
    /** 카드 비밀번호 앞 2자리 */
    password: z.string().regex(/^\d{2}$/),
    /** 카드 소유주 이름 — 페이업 결제 알림톡의 「{이름}님」 */
    holderName: z.string().trim().min(1).max(50),
    /** 결제 알림톡을 받을 휴대폰(선택). 비우면 알림톡을 보내지 않는다 */
    holderPhone: z.preprocess(
      (v) => (v === '' ? undefined : v),
      z
        .string()
        .regex(/^\d{11}$/)
        .optional(),
    ),
  })
  .strict();
export type CardInput = z.infer<typeof CardInput>;

/** 내 카드 — 결제자 화면은 **내 카드만** 싣는다 */
export const Card = z.object({
  id: z.uuid(),
  cardName: z.string(),
  last4: z.string(),
  expiryMmyy: z.string(),
  holderName: z.string(),
  createdAt: IsoDateTime,
});
export type Card = z.infer<typeof Card>;

/** 결제 금액 — 공급가 + VAT(원 미만 절사). 화면이 계산하지 않는다 */
export const Price = z.object({
  supplyKrw: z.number().int(),
  vatKrw: z.number().int(),
  amountKrw: z.number().int(),
});
export type Price = z.infer<typeof Price>;

/**
 * 구독이 대가로 받는 질문 (261013-subscription-query-link). 그 브랜드 자신의 질문이라 결제자 응답에 실어도 된다.
 * `deleted` — 묶인 뒤 질문이 지워졌다(구독은 남는다. 운영자가 다른 질문으로 바꾼다)
 */
export const SubscriptionQuery = z.object({ id: z.uuid(), query: z.string(), deleted: z.boolean() });
export type SubscriptionQuery = z.infer<typeof SubscriptionQuery>;

/** 결제자 화면의 구독 한 건 */
export const PayerSubscription = z.object({
  id: z.uuid(),
  brandId: z.uuid(),
  brandName: z.string(),
  name: z.string(),
  /** 묶인 질문. DB 는 선택이라 null 일 수 있다 — 지금은 운영자 화면이 반드시 고르게 한다 */
  query: SubscriptionQuery.nullable(),
  price: Price,
  status: z.enum(SUBSCRIPTION_STATUSES),
  /** 결제자(카드 주인 계정) — 아직 시작 전이면 null */
  payer: z
    .object({
      /** 이 화면의 사용자가 결제하는 구독인가 — 아니면 카드 정보를 싣지 않는다 */
      mine: z.boolean(),
      /** 남이 결제하면 그 소속자의 이름(없으면 회사명·가린 이메일) */
      name: z.string().nullable(),
      card: z.object({ cardName: z.string(), last4: z.string() }).nullable(),
    })
    .nullable(),
  /** 운영자가 정한 구독 개시일 — 없으면 결제한 날이 기준 */
  startOn: KstDate.nullable(),
  anchorDay: z.number().int().nullable(),
  currentPeriodStart: KstDate.nullable(),
  currentPeriodEnd: KstDate.nullable(),
  nextChargeOn: KstDate.nullable(),
  /**
   * 마지막 이용일(그날까지 포함) — 끝나는 날의 유일한 원천(261015-subscription-unify). null 이면 이어진다.
   * 기간 끝 해지면 이번 기간 끝날이고, 운영자가 아무 날로 정할 수도 있다
   */
  endOn: KstDate.nullable(),
  /**
   * ★ 카드가 **없는** 구독은 false 다 — 결제 수단은 구독의 종류가 아니다(결제 없이 이어진다, 261015-subscription-unify).
   * 지금 카드로는 다음 결제를 할 수 없다 — 카드 주인이 브랜드 소속에서 빠졌거나 카드가 지워졌다. 카드를 바꿔야 한다.
   * (결제 시점에 다시 판정하므로 바꾸지 않으면 다음 결제일에 실패로 남는다)
   */
  needsCard: z.boolean(),
  /** 마지막 결제가 실패했으면 그 사유. `blocked` 면 운영자 확인 전까지 결제할 수 없다 */
  lastFailure: z
    .object({
      reason: z.enum(FAILURE_REASONS),
      message: z.string(),
      at: IsoDateTime,
      blocked: z.boolean(),
    })
    .nullable(),
  startedAt: IsoDateTime.nullable(),
});
export type PayerSubscription = z.infer<typeof PayerSubscription>;

export const getBilling = defineRoute({
  method: 'get',
  path: '/api/billing',
  access: 'payer',
  summary: '내 결제 화면 — 내 브랜드의 제안·구독(결제 범위 전부)과 내 카드',
  response: z.object({
    subscriptions: z.array(PayerSubscription),
    cards: z.array(Card),
    /** 테스트 결제 환경(페이업 개발 서버)이면 true — 화면에 띠를 단다 */
    testMode: z.boolean(),
    /** 결제 기능이 준비되지 않았다(키 없음) — 시작·카드 등록 버튼을 「준비 중」으로 둔다 */
    available: z.boolean(),
  }),
});

export const registerCard = defineRoute({
  method: 'post',
  path: '/api/billing/cards',
  access: 'payer',
  summary: '카드 등록 — 내 계정의 카드. 내가 소속된 브랜드 전부의 구독에 쓸 수 있다',
  body: z.object({ card: CardInput }).strict(),
  response: z.object({ card: Card }),
});

export const deleteCard = defineRoute({
  method: 'delete',
  path: '/api/billing/cards/{id}',
  access: 'payer',
  summary: '카드 삭제 — 진행 중인 구독이 쓰는 카드는 지울 수 없다',
  params: IdParams,
  response: Ok,
});

/** 구독 서비스 이용약관(정기결제 포함) 동의 — 화면이 요약을 보여 주고 전문 링크와 함께 체크받는다 */
const Consent = z.literal(true, { error: '이용약관에 동의해 주세요' });

export const startSubscription = defineRoute({
  method: 'post',
  path: '/api/billing/subscriptions/{id}/start',
  access: 'payer',
  summary: '제안된 구독 시작 — 고른 카드로 첫 결제를 한다. 0 원 구독은 결제 없이 시작한다(카드 선택)',
  params: IdParams,
  body: z
    .object({
      /** 0 원 구독은 비워도 된다 — 결제하지 않는다. 그 밖에는 필수(서버가 400) */
      cardId: z.uuid().nullable(),
      consent: Consent,
      /** 화면이 보여 준 약관의 판 — 현재 판과 다르면 400(약관이 바뀐 뒤 옛 화면에서 동의하는 일이 없게) */
      termsVersion: z.literal(SUBSCRIPTION_TERMS_VERSION, {
        error: '이용약관이 바뀌었습니다. 화면을 새로 고친 뒤 다시 동의해 주세요',
      }),
    })
    .strict(),
  response: z.object({ subscription: PayerSubscription }),
});

export const changeCard = defineRoute({
  method: 'post',
  path: '/api/billing/subscriptions/{id}/card',
  access: 'payer',
  summary:
    '결제 카드 교체 — 결제가 밀린 구독이면 바꾼 카드로 바로 한 번 결제한다. 카드 없이 이어지던 구독은 카드를 붙이면 다음 결제일부터 자동결제다(동의 필요)',
  params: IdParams,
  body: z
    .object({
      cardId: z.uuid(),
      /** 카드 없이 이어지던 구독에 처음 붙일 때만 필요하다 — 이때부터 정기결제라 약관 동의를 받는다 */
      consent: Consent.optional(),
      termsVersion: z
        .literal(SUBSCRIPTION_TERMS_VERSION, {
          error: '이용약관이 바뀌었습니다. 화면을 새로 고친 뒤 다시 동의해 주세요',
        })
        .optional(),
    })
    .strict(),
  response: z.object({ subscription: PayerSubscription }),
});

export const retrySubscription = defineRoute({
  method: 'post',
  path: '/api/billing/subscriptions/{id}/retry',
  access: 'payer',
  summary: '다시 결제 — 결제가 밀린 구독을 지금 카드로 한 번 결제한다. 카드 주인만(결과 확인 대기 건은 운영자만)',
  params: IdParams,
  response: z.object({ subscription: PayerSubscription }),
});

/** 결제 내역 한 쪽의 크기 — 화면과 서버가 같은 값을 본다 */
export const PAYER_CHARGES_PAGE_SIZE = 10;

/**
 * 결제자 화면의 결제 한 건 — **운영자 모양(`AdminCharge`)에서 좁힌 것**이다.
 * ★ PG 응답·거래번호·승인번호·검토자·환불 처리자를 싣지 않는다(위 머리 주석).
 * ★ 남이 낸 결제(같은 브랜드의 다른 소속자)에는 카드 정보를 싣지 않고 누가 냈는지만 — 결제자 화면 규칙과 같다(`PayerSubscription.payer`).
 */
export const PayerCharge = z.object({
  id: z.uuid(),
  brandName: z.string(),
  subscriptionName: z.string(),
  kind: z.enum(['initial', 'renewal']),
  periodStart: KstDate,
  periodEnd: KstDate,
  /** pending 처리 중 · paid 결제 완료 · failed 실패 · refunded 환불 */
  status: z.enum(CHARGE_STATUSES),
  /** 결과를 아직 모르는 결제(무응답, 운영자 확인 전) — 「확인 중」으로 보인다 */
  checking: z.boolean(),
  price: Price,
  payer: z.object({
    mine: z.boolean(),
    /** 남이 낸 결제면 그 사람 이름(없으면 가린 이메일) */
    name: z.string().nullable(),
    card: z.object({ cardName: z.string(), last4: z.string() }).nullable(),
  }),
  /** 결제된 시각(실패면 시도한 시각) */
  at: IsoDateTime,
  refundedAt: IsoDateTime.nullable(),
});
export type PayerCharge = z.infer<typeof PayerCharge>;

export const listMyCharges = defineRoute({
  method: 'get',
  path: '/api/billing/charges',
  access: 'payer',
  summary: '내 결제 내역 — 결제 범위 브랜드 전부의 결제 기록, 최신순 쪽 단위',
  query: z.object({ page: z.coerce.number().int().min(1).max(10_000).optional() }),
  response: z.object({
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    charges: z.array(PayerCharge),
  }),
});

// ── 운영자 ───────────────────────────────────────────────────────────────

export const AdminCharge = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  kind: z.enum(['initial', 'renewal']),
  periodStart: KstDate,
  periodEnd: KstDate,
  status: z.enum(CHARGE_STATUSES),
  failureReason: z.enum(FAILURE_REASONS).nullable(),
  price: Price,
  /** 결제자 계정의 이메일 — 운영자 화면 전용 */
  payerEmail: z.string().nullable(),
  card: z.object({ cardName: z.string(), last4: z.string() }).nullable(),
  /** 페이업 관리자 화면에서 찾는 열쇠 — 같은 가맹점을 selfad 와 함께 쓴다 */
  orderNumber: z.string(),
  pgTransactionId: z.string().nullable(),
  pgAuthNumber: z.string().nullable(),
  pgCode: z.string().nullable(),
  pgMessage: z.string().nullable(),
  triggeredBy: z.string().nullable(),
  reviewedAt: IsoDateTime.nullable(),
  reviewedBy: z.string().nullable(),
  refundedAt: IsoDateTime.nullable(),
  refundedBy: z.string().nullable(),
  refundReason: z.string().nullable(),
  paidAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type AdminCharge = z.infer<typeof AdminCharge>;

export const AdminSubscription = PayerSubscription.omit({ payer: true }).extend({
  brandStatus: z.string(),
  payer: z
    .object({
      userId: z.uuid(),
      email: z.string(),
      name: z.string().nullable(),
      card: z.object({ cardName: z.string(), last4: z.string() }),
    })
    .nullable(),
  cancelReason: z.enum(CANCEL_REASONS).nullable(),
  canceledAt: IsoDateTime.nullable(),
  canceledBy: z.string().nullable(),
  consentedAt: IsoDateTime.nullable(),
  /** 동의한 구독 서비스 이용약관의 판 */
  termsVersion: z.string().nullable(),
  startedBy: z.string().nullable(),
  createdBy: z.string().nullable(),
  priceChangedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  /**
   * 열린 결제 링크(미사용·미회수·미만료) — **운영자 계약에만** 있다(261020-pay-link 「발전」). `url` 은 저장된 암호문을
   * 풀어 만든다. null = 다시 보여 줄 수 없는 링크(암호문이 생기기 전·결제 키 없음) — 화면은 「새로 만들기」를 안내한다
   */
  payLink: z
    .object({
      id: z.uuid(),
      userName: z.string(),
      expiresAt: IsoDateTime,
      attempts: z.number().int(),
      url: z.string().nullable(),
    })
    .nullable(),
});
export type AdminSubscription = z.infer<typeof AdminSubscription>;

export const SubscriptionOffer = z
  .object({
    name: z.string().trim().min(1, '구독 이름을 넣어 주세요').max(100),
    /** 월 공급가(원, VAT 별도). **0 원 허용** — 무료 구독은 결제하지 않는다(261015-subscription-unify) */
    supplyPriceKrw: z.number().int().min(0).max(SUPPLY_PRICE_MAX),
    /**
     * 구독 개시일(선택) — 결제가 늦어도 첫 기간과 매월 결제일은 이 날 기준이다(10/20 개시·10/21 결제 → 10/20~11/19).
     * 한 기간 넘게 늦으면 지나간 기간은 건너뛴다. 비우면 결제한 날이 기준이다
     */
    startOn: KstDate.nullable().default(null),
    /**
     * 이 구독이 대가로 받는 질문 — 같은 브랜드의 살아 있는 질문이어야 한다(아니면 400).
     * 계약은 null 을 받는다(질문과 무관한 구독을 대비한 DB 설계와 같다). **지금은 운영자 화면이 반드시 고르게 한다**
     */
    queryId: z.uuid().nullable(),
  })
  .strict();
export type SubscriptionOffer = z.infer<typeof SubscriptionOffer>;

export const listAdminSubscriptions = defineRoute({
  method: 'get',
  path: '/api/admin/billing/subscriptions',
  access: 'admin',
  summary: '구독 전체(고객을 주면 그 고객만) + 개입 대기(확인 필요·결제 밀림·오래된 제안)',
  query: BrandKindQuery.extend({ brandId: z.uuid().optional() }),
  response: z.object({
    subscriptions: z.array(AdminSubscription),
    /** 확인 필요 — 결과를 모르는 결제(no_response, 미검토) */
    unreviewed: z.array(AdminCharge),
    testMode: z.boolean(),
    available: z.boolean(),
  }),
});

/** 운영자 결제 내역 한 건 — 운영자 모양 그대로에 브랜드·구독 이름을 붙였다 */
export const AdminChargeListItem = AdminCharge.extend({ brandName: z.string(), subscriptionName: z.string() });
export type AdminChargeListItem = z.infer<typeof AdminChargeListItem>;
export const ADMIN_CHARGES_PAGE_SIZE = 20;

export const listAdminCharges = defineRoute({
  method: 'get',
  path: '/api/admin/billing/charges',
  access: 'admin',
  summary: '결제 내역 — 전체(브랜드를 주면 그 브랜드만), 최신순 쪽 단위',
  query: BrandKindQuery.extend({
    brandId: z.uuid().optional(),
    page: z.coerce.number().int().min(1).max(10_000).optional(),
  }),
  response: z.object({
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    charges: z.array(AdminChargeListItem),
  }),
});

export const getAdminSubscription = defineRoute({
  method: 'get',
  path: '/api/admin/billing/subscriptions/{id}',
  access: 'admin',
  summary: '구독 한 건과 결제 이력 전부',
  params: IdParams,
  response: z.object({ subscription: AdminSubscription, charges: z.array(AdminCharge) }),
});

export const offerSubscription = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions',
  access: 'admin',
  summary: '구독 제안 — 승인된 고객(active)이나 결제 대기 고객(confirmed)에게',
  body: SubscriptionOffer.extend({ brandId: z.uuid() }).strict(),
  response: z.object({ subscription: AdminSubscription }),
});

export const updateSubscription = defineRoute({
  method: 'patch',
  path: '/api/admin/billing/subscriptions/{id}',
  access: 'admin',
  summary:
    '이름·공급가·질문·마지막 이용일 수정 — 진행 중 구독의 가격은 **다음 결제부터** 적용(30일 전 통지는 운영자 몫). 개시일은 시작 전에만',
  params: IdParams,
  body: SubscriptionOffer.extend({
    /** 마지막 이용일(그날까지). null 이면 이어진다. 생략하면 그대로 둔다. 지나면 정기 단계가 끝낸다 */
    endOn: KstDate.nullable().optional(),
  }).strict(),
  response: z.object({ subscription: AdminSubscription }),
});

export const cancelSubscription = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions/{id}/cancel',
  access: 'admin',
  summary: '구독 해지(운영자 전용) — 즉시 또는 이번 기간 끝. 제안 상태면 철회',
  params: IdParams,
  body: z
    .object({
      when: z.enum(['now', 'period_end']),
      reason: z.enum(['requested', 'admin']),
    })
    .strict(),
  response: z.object({ subscription: AdminSubscription }),
});

export const undoCancelSubscription = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions/{id}/undo-cancel',
  access: 'admin',
  summary: '기간 끝 해지 예약 취소',
  params: IdParams,
  response: z.object({ subscription: AdminSubscription }),
});

export const adminRetry = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions/{id}/retry',
  access: 'admin',
  summary: '운영자 재결제 — 결과 확인 대기 건이 있으면 먼저 검토해야 한다',
  params: IdParams,
  response: z.object({ subscription: AdminSubscription }),
});

export const reviewCharge = defineRoute({
  method: 'post',
  path: '/api/admin/billing/charges/{id}/review',
  access: 'admin',
  summary: '결과를 모르는 결제 검토 — 페이업 관리자 화면에서 확인한 결과(승인됨: 거래번호 / 승인 안 됨)',
  params: IdParams,
  body: z.discriminatedUnion('result', [
    z.object({ result: z.literal('paid'), transactionId: z.string().trim().min(4).max(40) }).strict(),
    z.object({ result: z.literal('not_paid') }).strict(),
  ]),
  response: z.object({ charge: AdminCharge }),
});

export const refundCharge = defineRoute({
  method: 'post',
  path: '/api/admin/billing/charges/{id}/refund',
  access: 'admin',
  summary: '결제 취소·환불(운영자 전용) — 페이업은 **건별 전액**만 된다. 구독 상태는 바꾸지 않는다',
  params: IdParams,
  body: z.object({ reason: z.string().trim().min(1).max(300) }).strict(),
  response: z.object({ charge: AdminCharge }),
});

export const startWithoutPayment = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions/{id}/start',
  access: 'admin',
  summary:
    '결제 없이 시작 — 제안(offered)을 카드 없이 시작한다(오프라인 청구·무료). 결제 대기 브랜드면 이용을 연다. 기간은 개시일 기준',
  params: IdParams,
  body: z
    .object({
      /** 개시일 — 생략하면 제안의 개시일, 그것도 없으면 오늘. 지난 날도 된다(지나간 기간은 건너뛴다) */
      startOn: KstDate.nullable().default(null),
    })
    .strict(),
  response: z.object({ subscription: AdminSubscription }),
});

// ── 결제 링크 (261020-pay-link) ─────────────────────────────────────────

/**
 * 결제 링크 — 로그인 없이 카드 등록·구독 결제를 하는 일회성 주소. 운영자가 구독 하나와 그 브랜드의 소속 계정 하나를
 * 골라 발급하고 주소를 직접 보낸다(운영자 화면이 열린 링크의 주소를 계속 보여 준다 — 암호문 저장). 그 주소로 등록한
 * 카드는 **그 계정의 카드**다.
 * 할 수 있는 일은 둘뿐이다: 제안(offered)의 첫 결제, 결제 밀림(past_due)의 카드 교체 + 결제. 세션을 만들지 않는다.
 * ★ 토큰은 **body 로만** 받는다(경로·쿼리는 접근 로그에 남는다). 무효 토큰은 400 하나 — 만료·회수·사용됨을 가르지 않는다
 */
export const PAY_LINK_HOURS = 72;
/** 링크 하나로 카드를 제출할 수 있는 횟수 — 공개 카드 입력면이 카드번호 대입 시험 창구가 되지 않게 */
export const PAY_LINK_MAX_ATTEMPTS = 5;

export const PayLinkToken = z.string().min(20).max(200);

export const issuePayLink = defineRoute({
  method: 'post',
  path: '/api/admin/billing/subscriptions/{id}/pay-links',
  access: 'admin',
  summary:
    '결제 링크 발급 — 제안·결제 밀림 구독에, 카드 주인이 될 소속 계정을 골라 발급한다. 같은 구독의 열린 링크는 회수한다. 주소는 구독 응답(`payLink.url`)에서도 다시 볼 수 있다',
  params: IdParams,
  body: z.object({ userId: z.uuid() }).strict(),
  response: z.object({ url: z.string(), expiresAt: IsoDateTime }),
});

export const revokePayLink = defineRoute({
  method: 'delete',
  path: '/api/admin/billing/pay-links/{id}',
  access: 'admin',
  summary: '결제 링크 회수',
  params: IdParams,
  response: Ok,
});

/** 결제 링크 화면이 보여 줄 것 — 브랜드·구독·금액뿐이다. id·계정·카드 정보를 싣지 않는다 */
export const PayLinkView = z.object({
  /** start = 첫 결제, recover = 밀린 결제를 새 카드로 */
  kind: z.enum(['start', 'recover']),
  brandName: z.string(),
  subscriptionName: z.string(),
  /** 구독이 대가로 받는 질문 — 약관 요약(`SUBSCRIPTION_TERMS_SUMMARY`)의 {query} */
  query: z.string().nullable(),
  price: Price,
  /** 구독 개시일 — 약관 요약의 결제 일정 문구 */
  startOn: KstDate.nullable(),
  /** recover 일 때 마지막 실패 문구 */
  lastFailureMessage: z.string().nullable(),
  /** 결과 확인 대기(no_response 미검토) — 운영자가 확인하기 전에는 결제할 수 없다 */
  blocked: z.boolean(),
  attemptsLeft: z.number().int(),
  termsVersion: z.string(),
  testMode: z.boolean(),
});
export type PayLinkView = z.infer<typeof PayLinkView>;

export const inspectPayLink = defineRoute({
  method: 'post',
  path: '/api/pay-links/inspect',
  access: 'public',
  summary: '결제 링크 내용 확인',
  body: z.object({ token: PayLinkToken }).strict(),
  response: PayLinkView,
});

export const completePayLink = defineRoute({
  method: 'post',
  path: '/api/pay-links/complete',
  access: 'public',
  summary: '결제 링크로 카드 등록 + 결제 — 성공하면 링크가 소비된다. 거절이면 링크가 남아 다른 카드로 다시 할 수 있다',
  body: z
    .object({
      token: PayLinkToken,
      card: CardInput,
      consent: Consent,
      termsVersion: z.literal(SUBSCRIPTION_TERMS_VERSION, {
        error: '이용약관이 바뀌었습니다. 화면을 새로 고친 뒤 다시 동의해 주세요',
      }),
    })
    .strict(),
  response: z.object({
    brandName: z.string(),
    subscriptionName: z.string(),
    card: z.object({ cardName: z.string(), last4: z.string() }),
    /** 결제한 금액 — 0 원 구독은 0 */
    paidKrw: z.number().int(),
    nextChargeOn: KstDate.nullable(),
  }),
});
